import type { RecommenderStep } from '../Recommender/types';
import type { SingleSubmissionStep } from '../SingleProgramSubmission/types';
import type { Result } from '../../types/result';
import type { Question } from '../../types/types';
import type Lead from '../../types/leadDelivery';

export type SubmittedProgram = {
  program: {
    id: number;
    name: string;
  };
  school: {
    name: string;
    slug: string;
  };
  applicationCtaLabel?: string;
  applicationCtaUrl?: string;
};

// Controls for the Single Program Submission Slideout
export type SingleSubmissionSlideoutProps = {
  showSlideoutExperience: boolean;
  closeSlideoutExperience: () => void;
  openProgramSlideoutExperience: (
    result: Result,
    skipDuplicateCheck?: boolean,
  ) => void;
  onNextStepsClick?: (result: Result) => void;
  singleSubmissionExperienceStep: SingleSubmissionStep;
  onSlideoutProgramSubmit: () => void;
  goToSlideoutPiiConfirmation: () => void;
  goToSlideoutExitStrategy: () => void;
};

// Controls for the popup program submission experience (Recommender or Spotlight)
export type PopupExperienceProps = {
  popupExperienceStep: RecommenderStep;
  onPopupProgramClick: (result: Result) => void;
  showRecommender: boolean;
  showSpotlight: boolean;
  showPopupExperience: boolean;
  closePopupExperience: () => void;
  onPopupProgramSubmit: () => void;
  goToPopupProgramSelect: () => void;
};

export type ResultsProps = {
  resultsAreLoading: boolean;
  exactResults: Result[];
  relatedResults: Result[];
  recommendedResults: Result[];
  spotlightResults: Result[];
  selectedResult?: Result;
  title?: string;
  subtitle?: string;
};

export type PiiProps = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject?: string;
  category?: string;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setEmail: (email: string) => void;
  setPhone: (phone: string) => void;
  setFormErrors: (formErrors: { field: string }[]) => void;
  formErrors: { field: string }[];
  setEditMode: (editMode: boolean) => void;
  editMode: boolean;
  piiLeadErrors: { field: string }[];
  setPiiLeadErrors: (piiLeadErrors: { field: string }[]) => void;
};

export type InputProps = {
  startTime: number;
};

export enum SubmissionStatus {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED',
  SUBMITTING = 'SUBMITTING',
  ERROR = 'ERROR',
  UNQUALIFIED = 'UNQUALIFIED',
}

export type LeadSubmissionProps = {
  canSubmitLead: boolean;
  isHeclidLoading: boolean;
  leadSubmissionStatus?: SubmissionStatus;
  setLeadSubmissionStatus: (status: SubmissionStatus) => void;
};

// Generic partner identifiers read off the most recent LDS v4 submit
// response (EDUN-14483), e.g. `{ referenceId, studentId }`. Threaded into
// every surface that opens `applicationCtaURL` after submit (Recommender
// modal, post-submit Card "Next Steps") so `applicationCta`/`postLeadCta`
// fieldMap entries can resolve. `undefined` before any lead is submitted
// this session.
export type PartnerIdentifiersProps = {
  partnerIdentifiers?: Record<string, string>;
};

export type UserExperienceSurveyProps = {
  showUserExperienceSurvey: boolean;
};

export type AdditionalQuestionsProps = {
  showAdditionalQuestions: boolean;
  additionalQuestions: Question[];
  isLoadingQuestions: boolean;
  onAdditionalQuestionsSubmit: (
    formData: Record<string, string | number>,
  ) => void;
  lead: Lead;
};

export type SubmissionsProps = {
  submittedPrograms: SubmittedProgram[];
  submittedResults?: { programId?: number; schoolId?: string }[];
  onViewNextSteps?: (submission: SubmittedProgram) => void;
};

export type ResultsPageProps = SingleSubmissionSlideoutProps &
  PopupExperienceProps &
  ResultsProps &
  PiiProps &
  InputProps &
  LeadSubmissionProps &
  AdditionalQuestionsProps &
  SubmissionsProps &
  PartnerIdentifiersProps;
